/* eslint-disable max-lines */
/**
 * Single source of truth for every analytics event the web portal (apps/hakimo)
 * and the admin portal (apps/hakimo-admin) can send to Amplitude. Descriptions here are the ones to copy into Amplitude Data and the
 * Amplitude AI Context.
 *
 * Rules:
 * - Names are verb_noun in snake_case.
 * - Never rename an event that already has data: saved charts and dashboards
 *   point at the name. Set a display name in Amplitude, or merge old and new
 *   names with an Amplitude transformation.
 * - Every event needs a description saying when it fires and what it means.
 *   Set reviewed: true only after checking it against the code that fires it.
 * - status 'unused' means no code in this repo (web or admin portal) fires it,
 *   as of Oct 2026; it may still come from another app (mobile), so check
 *   before deleting.
 */

export type TrackingEventGroup =
  | 'app_navigation'
  | 'alarm'
  | 'location_alarm'
  | 'table'
  | 'sidebar'
  | 'page'
  | 'filter'
  | 'location'
  | 'door'
  | 'door_group'
  | 'alarm_type_group'
  | 'demo_alarm'
  | 'audio_device'
  | 'camera'
  | 'share_alarm'
  | 'status_stepper'
  | 'location_contact'
  | 'location_incident'
  | 'monitoring'
  | 'sop_workflow'
  | 'monitoring_window'
  | 'apartment_monitoring'
  | 'alarm_group_scan'
  | 'unified_monitoring'
  | 'alarm_group'
  | 'feedback';

export interface TrackingEventDefinition {
  /** Feature areas the event belongs to; drives the Event* group types below. */
  groups: readonly TrackingEventGroup[];
  /** When it fires and what it means. Becomes the Amplitude description. */
  description: string;
  /** Event properties sent with it (documentation only for now). */
  properties: readonly string[];
  status: 'active' | 'unused';
  /** true once the description has been checked against the firing code. */
  reviewed: boolean;
}

export const TRACKING_EVENTS = {
  navigate_alarms: {
    groups: ['app_navigation'],
    description:
      'Route change to the /alarms (legacy alarm list) page. Fires once per pathname change (useAmplitude), at the same time as [Amplitude] Page Viewed for that path.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  navigate_location_alarms: {
    groups: ['app_navigation'],
    description:
      'Route change to the /location-alarms page. Fires once per pathname change (useAmplitude), at the same time as [Amplitude] Page Viewed for that path.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  navigate_doors: {
    groups: ['app_navigation'],
    description:
      'Route change to the /doors page. Fires once per pathname change (useAmplitude), at the same time as [Amplitude] Page Viewed for that path.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  navigate_cameras: {
    groups: ['app_navigation'],
    description:
      'Route change to the /cameras page. Fires once per pathname change (useAmplitude), at the same time as [Amplitude] Page Viewed for that path.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  navigate_locations: {
    groups: ['app_navigation'],
    description:
      'Route change to the /locations page. Fires once per pathname change (useAmplitude), at the same time as [Amplitude] Page Viewed for that path.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  navigate_door_groups: {
    groups: ['app_navigation'],
    description:
      'Route change to the /doorgroups page. Fires once per pathname change (useAmplitude), at the same time as [Amplitude] Page Viewed for that path.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  navigate_users: {
    groups: ['app_navigation'],
    description:
      'Route change to the /users page. Fires once per pathname change (useAmplitude), at the same time as [Amplitude] Page Viewed for that path.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  navigate_audit_log: {
    groups: ['app_navigation'],
    description:
      'Route change to the /audit-log page. Fires once per pathname change (useAmplitude), at the same time as [Amplitude] Page Viewed for that path.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  navigate_monitoring: {
    groups: ['app_navigation'],
    description:
      'Route change to the /monitoring page. Fires once per pathname change (useAmplitude), at the same time as [Amplitude] Page Viewed for that path.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  navigate_insights: {
    groups: ['app_navigation'],
    description:
      'Route change to the /insights page. Fires once per pathname change (useAmplitude), at the same time as [Amplitude] Page Viewed for that path.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  open_alarm_filters_panel: {
    groups: ['alarm'],
    description:
      'Opened alarm filters panel. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_alarm_actions_menu: {
    groups: ['alarm'],
    description:
      'Opened alarm actions menu. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_bug_report: {
    groups: ['alarm'],
    description:
      'Submitted add bug report. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_add_bug_report: {
    groups: ['alarm'],
    description:
      'Cancelled add bug report. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_alarm_feedback: {
    groups: ['alarm'],
    description:
      'Submitted add alarm feedback. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_add_alarm_feedback: {
    groups: ['alarm'],
    description:
      'Cancelled add alarm feedback. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_alarm_comment: {
    groups: ['alarm'],
    description:
      'Submitted add alarm comment. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_add_alarm_comment: {
    groups: ['alarm'],
    description:
      'Cancelled add alarm comment. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_add_alarm_comment_modal: {
    groups: ['alarm'],
    description:
      'Opened add alarm comment modal. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_alarm_status_update: {
    groups: ['alarm'],
    description:
      'Click on Submit in the legacy alarm status modal. Logged on click, before the update is saved, so it includes failed updates and non-resolve status changes. For successful resolves use resolve_alarm.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  cancel_alarm_status_update: {
    groups: ['alarm'],
    description: 'Click on Cancel in the legacy alarm status modal.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  open_alarm_status_update_modal: {
    groups: ['alarm'],
    description:
      'Click on the status button in legacy alarm details, opening the status update modal. Intent step before submit_alarm_status_update.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  toggle_debug_mode: {
    groups: ['alarm'],
    description:
      'Toggled debug mode. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  toggle_live_mode: {
    groups: ['alarm'],
    description:
      'Toggled live mode. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  download_alarm_media: {
    groups: ['alarm'],
    description:
      'Downloaded alarm media. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_audio_recording: {
    groups: ['alarm'],
    description:
      'Submitted audio recording. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  view_alarm_details: {
    groups: ['location_alarm'],
    description:
      'Legacy alarm details page opened from the alarm list. time_to_view is milliseconds from the alarm appearing in the list to opening it.',
    properties: ['alarm_id', 'time_to_view'],
    status: 'active',
    reviewed: true,
  },
  view_location_alarm_details: {
    groups: ['location_alarm'],
    description:
      'Location alarm details opened from the location alarm list. time_to_view is milliseconds from the alarm appearing in the list to opening it.',
    properties: ['alarm_id', 'time_to_view'],
    status: 'active',
    reviewed: true,
  },
  resolve_location_alarm: {
    groups: ['location_alarm'],
    description:
      'Location alarm status changed from the location alarm overview (fires in the status-change callback). time_to_resolve is milliseconds since the overview opened.',
    properties: ['alarm_id', 'time_to_resolve'],
    status: 'active',
    reviewed: true,
  },
  resolve_alarm: {
    groups: ['location_alarm'],
    description:
      'Legacy alarm successfully set to Resolved; fires after the update succeeds. time_to_resolve is milliseconds since the details page opened. Preferred count of legacy resolves.',
    properties: ['alarm_id', 'time_to_resolve'],
    status: 'active',
    reviewed: true,
  },
  submit_location_alarm_audio_recording: {
    groups: ['location_alarm'],
    description:
      'Submitted location alarm audio recording. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  update_visible_columns: {
    groups: ['table'],
    description:
      'Updated visible columns. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  move_next_page: {
    groups: ['table'],
    description:
      'Moved next page. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  move_previous_page: {
    groups: ['table'],
    description:
      'Moved previous page. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  move_first_page: {
    groups: ['table'],
    description:
      'Moved first page. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  move_to_page_by_number: {
    groups: ['table'],
    description:
      'Moved to page by number. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  move_last_page: {
    groups: ['table'],
    description:
      'Moved last page. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  close_sidebar_mobile: {
    groups: ['sidebar'],
    description:
      'Closed sidebar mobile. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  collapse_sidebar: {
    groups: ['sidebar'],
    description:
      'Collapsed sidebar. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  expand_sidebar: {
    groups: ['sidebar'],
    description:
      'Expanded sidebar. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  navigate_back_page: {
    groups: ['page'],
    description:
      'Click on the Back arrow in a page header (ui-layout Page). UI interaction only; the navigation it causes is recorded separately.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  apply_appliances_filters: {
    groups: ['filter'],
    description:
      'Admin portal, appliances list: Apply clicked in the filter panel (FilterFooter in AppliancesFilterPanel). Logged on the click.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  reset_appliances_filters: {
    groups: ['filter'],
    description:
      'Admin portal, appliances list: Reset clicked in the filter panel (FilterFooter in AppliancesFilterPanel). Logged on the click.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  apply_alarms_filters: {
    groups: ['filter'],
    description:
      'Applied alarms filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_alarms_filters: {
    groups: ['filter'],
    description:
      'Reset alarms filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  apply_location_alarms_filters: {
    groups: ['filter'],
    description:
      'Applied location alarms filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_location_alarms_filters: {
    groups: ['filter'],
    description:
      'Reset location alarms filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  apply_monitoring_alarms_filters: {
    groups: ['filter'],
    description:
      'Applied monitoring alarms filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  reset_monitoring_alarms_filters: {
    groups: ['filter'],
    description:
      'Reset monitoring alarms filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  apply_door_groups_filters: {
    groups: ['filter'],
    description:
      'Applied door groups filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_door_groups_filters: {
    groups: ['filter'],
    description:
      'Reset door groups filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  apply_alarm_type_group_filters: {
    groups: ['filter', 'alarm_type_group'],
    description:
      'Applied alarm type group filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  clear_alarm_type_group_filters: {
    groups: ['filter', 'alarm_type_group'],
    description:
      'Cleared alarm type group filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  apply_doors_filters: {
    groups: ['filter'],
    description:
      'Applied doors filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_doors_filters: {
    groups: ['filter'],
    description:
      'Reset doors filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  apply_locations_filters: {
    groups: ['filter'],
    description:
      'Applied locations filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_locations_filters: {
    groups: ['filter'],
    description:
      'Reset locations filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  apply_users_filters: {
    groups: ['filter'],
    description:
      'Applied users filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_users_filters: {
    groups: ['filter'],
    description:
      'Reset users filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  apply_cam_filters: {
    groups: ['filter'],
    description:
      'Applied cam filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_cam_filters: {
    groups: ['filter'],
    description:
      'Reset cam filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  apply_audio_filters: {
    groups: ['filter'],
    description:
      'Applied audio filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_audio_filters: {
    groups: ['filter'],
    description:
      'Reset audio filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  use_filters: {
    groups: ['filter'],
    description:
      'Filters applied on a list page (alarms, location alarms, alarm groups, cameras, doors, users, audio devices, escalations, multi-camera). filters holds the applied values. Fires on the same click as the page-specific apply_*_filters event.',
    properties: ['filters'],
    status: 'active',
    reviewed: true,
  },
  apply_escalation_list_filters: {
    groups: ['filter'],
    description:
      'Applied escalation list filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_escalation_list_filters: {
    groups: ['filter'],
    description:
      'Reset escalation list filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  apply_forensics_filters: {
    groups: ['filter'],
    description:
      'Applied forensics filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_forensics_filters: {
    groups: ['filter'],
    description:
      'Reset forensics filters. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  apply_tenants_filters: {
    groups: ['filter'],
    description:
      'Admin portal, tenants list: Apply clicked in the filter panel (FilterFooter in tenants FilterPanelContent). Logged on the click.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  reset_tenants_filters: {
    groups: ['filter'],
    description:
      'Admin portal, tenants list: Reset clicked in the filter panel (FilterFooter in tenants FilterPanelContent). Logged on the click.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  open_add_location_modal: {
    groups: ['location'],
    description:
      'Opened add location modal. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_location_filters_panel: {
    groups: ['location'],
    description:
      'Opened location filters panel. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_location: {
    groups: ['location'],
    description:
      'Submitted add location. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  cancel_add_location: {
    groups: ['location'],
    description:
      'Cancelled add location. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  save_site_schedule: {
    groups: ['location'],
    description:
      'Saved site schedule. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_delete_door_camera_mapping: {
    groups: ['door'],
    description:
      'Submitted delete door camera mapping. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_delete_door_camera_mapping: {
    groups: ['door'],
    description:
      'Cancelled delete door camera mapping. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_add_doors_to_location_modal: {
    groups: ['door'],
    description:
      'Opened add doors to location modal. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_add_doors_to_door_group_modal: {
    groups: ['door'],
    description:
      'Opened add doors to door group modal. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_door_filters_panel: {
    groups: ['door'],
    description:
      'Opened door filters panel. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_add_door_camera_mapping_modal: {
    groups: ['door'],
    description:
      'Opened add door camera mapping modal. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  remove_door_from_door_group: {
    groups: ['door'],
    description:
      'Removed door from door group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_remove_door_from_door_group: {
    groups: ['door'],
    description:
      'Submitted remove door from door group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_remove_door_from_door_group: {
    groups: ['door'],
    description:
      'Cancelled remove door from door group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_doors_to_location: {
    groups: ['door'],
    description:
      'Submitted add doors to location. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_add_doors_to_location: {
    groups: ['door'],
    description:
      'Cancelled add doors to location. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_doors_to_door_group: {
    groups: ['door'],
    description:
      'Submitted add doors to door group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_add_doors_to_door_group: {
    groups: ['door'],
    description:
      'Cancelled add doors to door group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_door_camera_mapping: {
    groups: ['door'],
    description:
      'Submitted add door camera mapping. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_add_door_camera_mapping: {
    groups: ['door'],
    description:
      'Cancelled add door camera mapping. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  fetch_next_door_label_details: {
    groups: ['door'],
    description:
      'Fetched next door label details. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  save_door_label_details: {
    groups: ['door'],
    description:
      'Saved door label details. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_door_label_details: {
    groups: ['door'],
    description:
      'Reset door label details. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  clear_door_label_details: {
    groups: ['door'],
    description:
      'Cleared door label details. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_add_door_group_modal: {
    groups: ['door_group'],
    description:
      'Opened add door group modal. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_door_group_filters_panel: {
    groups: ['door_group'],
    description:
      'Opened door group filters panel. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_door_group: {
    groups: ['door_group'],
    description:
      'Submitted add door group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_add_door_group: {
    groups: ['door_group'],
    description:
      'Cancelled add door group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_modify_processing_config: {
    groups: ['door_group'],
    description:
      'Submitted modify processing config. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_modify_processing_config: {
    groups: ['door_group'],
    description:
      'Cancelled modify processing config. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_update_door_group_details: {
    groups: ['door_group'],
    description:
      'Submitted update door group details. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_update_door_group_details: {
    groups: ['door_group'],
    description:
      'Cancelled update door group details. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_add_alarm_type_group_modal: {
    groups: ['alarm_type_group'],
    description:
      'Opened add alarm type group modal. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_alarm_type_group_filters_panel: {
    groups: ['alarm_type_group'],
    description:
      'Opened alarm type group filters panel. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_alarm_type_group: {
    groups: ['alarm_type_group'],
    description:
      'Submitted add alarm type group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_presence_rule: {
    groups: ['alarm_type_group'],
    description:
      'Submitted add presence rule. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_add_presence_rule: {
    groups: ['alarm_type_group'],
    description:
      'Cancel clicked in the Add presence rule form on the camera details page (AddEditPresenceRule, not editing).',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  submit_update_presence_rule: {
    groups: ['alarm_type_group'],
    description:
      'Submitted update presence rule. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_update_presence_rule: {
    groups: ['alarm_type_group'],
    description:
      'Cancel clicked in the Edit presence rule form on the camera details page (AddEditPresenceRule, editing).',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  cancel_add_alarm_type_group: {
    groups: ['alarm_type_group'],
    description:
      'Cancel clicked in the Add alarm type group form (AddEditAlarmTypeGroup, not editing).',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  submit_update_alarm_type_group_details: {
    groups: ['alarm_type_group'],
    description:
      'Submitted update alarm type group details. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_update_alarm_type_group_details: {
    groups: ['alarm_type_group'],
    description:
      'Cancel clicked in the Edit alarm type group form (AddEditAlarmTypeGroup, editing).',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  submit_delete_alarm_type_group: {
    groups: ['alarm_type_group'],
    description:
      'Submitted delete alarm type group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_delete_alarm_type_group: {
    groups: ['alarm_type_group'],
    description:
      'Cancelled delete alarm type group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_alarm_types_to_group: {
    groups: ['alarm_type_group'],
    description:
      'Submitted add alarm types to group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_add_alarm_types_to_group: {
    groups: ['alarm_type_group'],
    description:
      'Cancelled add alarm types to group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_remove_alarm_type_from_alarm_type_group: {
    groups: ['alarm_type_group'],
    description:
      'Submitted remove alarm type from alarm type group. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  submit_demo_alarm_ingest: {
    groups: ['demo_alarm'],
    description:
      'Submitted demo alarm ingest. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  view_demo_alarm: {
    groups: ['demo_alarm'],
    description:
      'Viewed demo alarm. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_add_audio_device_modal: {
    groups: ['audio_device'],
    description:
      'Opened add audio device modal. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_audio_device: {
    groups: ['audio_device'],
    description:
      'Submitted add audio device. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_add_audio_device: {
    groups: ['audio_device'],
    description:
      'Cancelled add audio device. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_add_audio_cam_mapping: {
    groups: ['audio_device'],
    description:
      'Submitted add audio cam mapping. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_add_audio_cam_mapping: {
    groups: ['audio_device'],
    description:
      'Cancelled add audio cam mapping. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  update_automated_talkdown: {
    groups: ['audio_device'],
    description:
      'Updated automated talkdown. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  open_audio_devices_filters_panel: {
    groups: ['audio_device'],
    description:
      'Opened audio devices filters panel. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  update_sps_setting: {
    groups: ['audio_device'],
    description:
      'Updated sps setting. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  new_camera_deadzones: {
    groups: ['camera'],
    description:
      'Started new camera deadzones. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  save_camera_deadzones: {
    groups: ['camera'],
    description:
      'Saved camera deadzones. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  reset_camera_deadzones: {
    groups: ['camera'],
    description:
      'Reset camera deadzones. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  clear_camera_deadzones: {
    groups: ['camera'],
    description:
      'Cleared camera deadzones. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  toggle_multi_cam_fullscreen: {
    groups: ['camera'],
    description:
      'Toggled multi cam fullscreen. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_camera_details_audio_talkdown: {
    groups: ['camera'],
    description:
      'Submitted camera details audio talkdown. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_manual_camera_audio_talkdown: {
    groups: ['camera'],
    description:
      'Submitted manual camera audio talkdown. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_text_to_speech_camera_talkdown: {
    groups: ['camera'],
    description:
      'Submitted text to speech camera talkdown. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  generate_text_to_speech: {
    groups: ['camera'],
    description:
      'Generated text to speech. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  update_automated_talkdown_time: {
    groups: ['camera'],
    description:
      'Updated automated talkdown time. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  generate_share_alarm_link: {
    groups: ['share_alarm'],
    description:
      'Generated share alarm link. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  close_generate_share_alarm_link_modal: {
    groups: ['share_alarm'],
    description:
      'Closed generate share alarm link modal. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  person_detected: {
    groups: ['status_stepper'],
    description: 'Person detected button in the legacy alarm status stepper.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  vehicle_detected: {
    groups: ['status_stepper'],
    description: 'Vehicle detected button in the legacy alarm status stepper.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  incorrect_detection: {
    groups: ['status_stepper'],
    description:
      'Marked incorrect detection. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  correct_detection: {
    groups: ['status_stepper'],
    description:
      'Marked correct detection. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  incorrect_detection_v2: {
    groups: ['status_stepper'],
    description:
      'Incorrect detection button in the legacy alarm status stepper.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  cancel_status_stepper: {
    groups: ['status_stepper'],
    description:
      'Cancelled status stepper. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  add_status_update_comment: {
    groups: ['status_stepper'],
    description:
      'Added status update comment. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  call_location_contact: {
    groups: ['location_contact'],
    description:
      'Called location contact. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  sms_location_contact: {
    groups: ['location_contact'],
    description:
      'Sent an SMS to location contact. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  create_location_incident: {
    groups: ['location_incident'],
    description:
      'Created location incident. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  close_and_resolve_active_location_incident: {
    groups: ['location_incident'],
    description:
      'Closed and resolve active location incident. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  get_monitoring_alarm: {
    groups: ['monitoring'],
    description:
      'Click on Get alarm on the /monitoring page, in both its views: the location-alarm screen (feature-alarms monitoring/PendingAlarm) and the alarm-group screen (feature-shared unified-alarm-group/MonitoringScreen). Logged on click with no properties.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  on_new_monitoring_alarm_load: {
    groups: ['monitoring'],
    description: 'New alarm loaded on the legacy monitoring screen.',
    properties: ['locationAlarmId'],
    status: 'active',
    reviewed: true,
  },
  sop_workflow_escalate_location_alarm_step_count: {
    groups: ['sop_workflow'],
    description:
      'Escalation from the SOP steps view. escalated_after_step_count is the step reached when escalating.',
    properties: ['locationAlarmId', 'escalated_after_step_count'],
    status: 'active',
    reviewed: true,
  },
  sop_workflow_step_action_yes: {
    groups: ['sop_workflow'],
    description: 'Yes answered on an SOP step.',
    properties: ['locationAlarmId', 'stepNumber', 'stepTitle', 'stepType'],
    status: 'active',
    reviewed: true,
  },
  sop_workflow_step_action_no: {
    groups: ['sop_workflow'],
    description: 'No answered on an SOP step.',
    properties: ['locationAlarmId', 'stepNumber', 'stepTitle', 'stepType'],
    status: 'active',
    reviewed: true,
  },
  update_monitoring_disarm_window: {
    groups: ['monitoring_window'],
    description:
      'Updated monitoring disarm window. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  call_apartment_contact: {
    groups: ['apartment_monitoring'],
    description: 'Voice call started to a site contact.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  resolve_apartment_monitoring_escalation: {
    groups: ['apartment_monitoring'],
    description: 'Confirm click in the scan escalation resolve form.',
    properties: [],
    status: 'active',
    reviewed: true,
  },
  event_received: {
    groups: ['apartment_monitoring'],
    description:
      'Event received. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  event_marked_safe: {
    groups: ['apartment_monitoring'],
    description: 'Legacy scan page: alarm group marked safe from the grid.',
    properties: ['alarmGroupId', 'tenantId'],
    status: 'active',
    reviewed: true,
  },
  event_snoozed: {
    groups: ['apartment_monitoring'],
    description:
      'Event snoozed. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  event_investigate: {
    groups: ['apartment_monitoring'],
    description:
      'Legacy scan page: Investigate opened for an alarm group from the grid.',
    properties: ['alarmGroupId', 'tenantId'],
    status: 'active',
    reviewed: true,
  },
  event_escalated: {
    groups: ['apartment_monitoring'],
    description: 'Legacy scan page: alarm group escalated from the grid.',
    properties: ['alarmGroupId', 'tenantId'],
    status: 'active',
    reviewed: true,
  },
  event_auto_removed: {
    groups: ['apartment_monitoring'],
    description:
      'Event auto removed. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  talkdown_submitted_for_escalation: {
    groups: ['apartment_monitoring'],
    description:
      'Talkdown submitted for escalation. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  event_triggering_camera_pop_up: {
    groups: ['apartment_monitoring'],
    description:
      'Event triggering camera pop up. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  scan_smart_rearrange: {
    groups: ['apartment_monitoring'],
    description:
      'Scan smart rearrange. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  camera_unsnoozed: {
    groups: ['apartment_monitoring'],
    description:
      'Camera unsnoozed. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  scan_camera_count: {
    groups: ['apartment_monitoring'],
    description:
      'Scan camera count. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  scan_error_adding_camera: {
    groups: ['apartment_monitoring'],
    description:
      'Scan error adding camera. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'unused',
    reviewed: false,
  },
  scan_error_adding_alarm_group: {
    groups: ['apartment_monitoring'],
    description:
      'Error telemetry: adding an alarm group to the scan or unified-monitoring grid failed.',
    properties: ['error'],
    status: 'active',
    reviewed: true,
  },
  scan_alarm_group_received: {
    groups: ['alarm_group_scan'],
    description:
      'Legacy scan page: an alarm group arrived over the websocket. One event per group per open screen.',
    properties: ['alarm_group_id', 'tenant_id', 'severity', 'username'],
    status: 'active',
    reviewed: true,
  },
  scan_alarm_group_rendered: {
    groups: ['alarm_group_scan'],
    description: 'Legacy scan page: an alarm group was rendered in the grid.',
    properties: ['alarm_group_id', 'tenant_id', 'severity', 'username'],
    status: 'active',
    reviewed: true,
  },
  scan_alarm_rendered_mismatch: {
    groups: ['alarm_group_scan'],
    description:
      'Legacy scan page diagnostic: the grid shows a different number of items than it has data for.',
    properties: ['shown_item_count', 'alarm_group_data_count', 'username'],
    status: 'active',
    reviewed: true,
  },
  scan_alarm_group_id_missing: {
    groups: ['alarm_group_scan'],
    description:
      'Legacy scan page diagnostic: a raw alarm arrived without an alarm group id.',
    properties: [
      'alarm_group_id',
      'camera_group_id',
      'raw_alarm_id',
      'tenant_id',
      'username',
    ],
    status: 'active',
    reviewed: true,
  },
  get_unified_monitoring_alarm: {
    groups: ['unified_monitoring'],
    description:
      'Outcome of a Get alarm click on /unified-monitoring, logged once when the response arrives (not on the click); the button stays disabled until then, so every response belongs to the pending click. pickup_step is assigned when alarm groups came back, no_alarm when the response was empty: the queue was already empty, another operator took the alarm groups first, or the backend could not load them. latency_ms is click to response; slow pickups are logged too. Before Oct 2026 this event was logged on the click itself, with no properties.',
    properties: [
      'pickup_step',
      'latency_ms',
      'alarm_group_count',
      'alarm_group_ids',
      'products',
    ],
    status: 'active',
    reviewed: true,
  },
  ai_recommendation_timestamp_seek_out_of_bounds: {
    groups: ['unified_monitoring'],
    description:
      'Error telemetry, not a user action: an AI recommendation timestamp points outside the alarm timeline, so seeking to it fails.',
    properties: [
      'timestamp',
      'target_timestamp',
      'timeline_start',
      'timeline_end',
      'context',
      'out_of_bounds_type',
      'alarm_group_id',
      'location_id',
      'tenant_id',
    ],
    status: 'active',
    reviewed: true,
  },
  unified_monitoring_mark_safe: {
    groups: ['unified_monitoring'],
    description:
      'Operator marks an alarm group safe on /unified-monitoring (action button or scan quick resolve). Logged on click, before the backend confirms. Exclude wasAlreadyResolved=true when counting resolves.',
    properties: ['alarmGroupId', 'product', 'wasAlreadyResolved'],
    status: 'active',
    reviewed: true,
  },
  unified_monitoring_investigate: {
    groups: ['unified_monitoring'],
    description:
      'Operator opens Investigate for an alarm group on /unified-monitoring. Logged on click.',
    properties: ['alarmGroupId', 'product', 'wasAlreadyResolved'],
    status: 'active',
    reviewed: true,
  },
  unified_monitoring_escalate: {
    groups: ['unified_monitoring'],
    description:
      'Operator escalates an alarm group on /unified-monitoring. Logged on click, before the backend confirms.',
    properties: ['alarmGroupId', 'product', 'wasAlreadyResolved'],
    status: 'active',
    reviewed: true,
  },
  unified_monitoring_resolve_escalation: {
    groups: ['unified_monitoring'],
    description:
      'Operator resolves an escalation on /unified-monitoring. Logged on click, before the backend confirms.',
    properties: ['alarmGroupId', 'product', 'wasAlreadyResolved'],
    status: 'active',
    reviewed: true,
  },
  unified_monitoring_close_alarm: {
    groups: ['unified_monitoring'],
    description:
      'Operator closes the open alarm group and returns to pending alarms. wasResolved=false means the alarm was dropped unresolved after a confirm prompt.',
    properties: ['alarmGroupId', 'product', 'wasResolved'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_resolve_submit: {
    groups: ['alarm_group'],
    description:
      'Submit in the alarm group header status editor, for scan and remote-guarding groups.',
    properties: ['alarmGroupId', 'product'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_copy_link: {
    groups: ['alarm_group'],
    description: 'Copy link action in the alarm group header.',
    properties: ['alarmGroupId', 'product'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_share: {
    groups: ['alarm_group'],
    description: 'Share action in the alarm group header.',
    properties: ['alarmGroupId', 'product'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_toggle_fullscreen: {
    groups: ['alarm_group'],
    description:
      'Alarm group view entered or left fullscreen (button or Esc). isFullScreen is the new state.',
    properties: ['alarmGroupId', 'product', 'isFullScreen'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_change_cam_mode: {
    groups: ['alarm_group'],
    description:
      'Camera feed mode switched in the alarm group header (for example alarm video or split view). mode is the selected mode.',
    properties: ['alarmGroupId', 'product', 'mode'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_add_camera: {
    groups: ['alarm_group'],
    description: 'Add camera from tenant clicked in the alarm group header.',
    properties: ['alarmGroupId', 'product'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_toggle_zone_overlay: {
    groups: ['alarm_group'],
    description:
      'Zone overlay type toggled in the alarm group header. zoneType is the toggled zone.',
    properties: ['alarmGroupId', 'product', 'zoneType'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_toggle_minor_alarms: {
    groups: ['alarm_group'],
    description:
      'Minor alarms toggle on the alarm group timeline. show is the new state.',
    properties: ['alarmGroupId', 'product', 'show'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_load_neighbour_alarms: {
    groups: ['alarm_group'],
    description:
      'Neighbouring alarms loaded onto the alarm group timeline; fires when the response arrives. direction is next or prev.',
    properties: ['alarmGroupId', 'product', 'direction'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_playback_control: {
    groups: ['alarm_group'],
    description:
      'Playback control on the alarm group timeline. play/pause comes from the play button, clicking the video or frames, scrubbing the timeline (auto-pause) or clicking a detected face; rew/fwd/reset come from the timeline buttons.',
    properties: ['alarmGroupId', 'product', 'action'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_sop_navigate: {
    groups: ['alarm_group'],
    description:
      'SOP panel view change (steps, escalate, resolve). to is the target view; action is the quick-resolve action when opening resolve.',
    properties: ['alarmGroupId', 'product', 'to', 'action'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_sop_situation_select: {
    groups: ['alarm_group'],
    description:
      'Situation picked in the SOP workflow. situation is its label.',
    properties: ['alarmGroupId', 'product', 'situation'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_sop_quick_resolve_select: {
    groups: ['alarm_group'],
    description:
      'Quick-resolve button in the SOP workflow; action is the resolve reason. The same resolve is also logged as unified_monitoring_mark_safe (scan) or alarm_group_sop_resolve_submit (remote guarding, enterprise), so never add them together.',
    properties: ['alarmGroupId', 'product', 'action'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_sop_resolve_submit: {
    groups: ['alarm_group'],
    description:
      'Resolve submitted from the SOP workflow, logged before the update is saved. action and subAction hold the chosen reason; action is missing when the resolve came from the quick-resolve button.',
    properties: ['alarmGroupId', 'product', 'action', 'subAction'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_sop_pre_escalate_submit: {
    groups: ['alarm_group'],
    description:
      'Pre-escalation submitted from the SOP workflow. situation and subSituation hold the chosen labels.',
    properties: ['alarmGroupId', 'product', 'situation', 'subSituation'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_sop_step_change: {
    groups: ['alarm_group'],
    description: 'SOP workflow step changed. step is the new step index.',
    properties: ['alarmGroupId', 'product', 'step'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_sop_call_contact: {
    groups: ['alarm_group'],
    description:
      'Call to a site contact placed from the SOP workflow. tag is the contact tag.',
    properties: ['alarmGroupId', 'product', 'tag'],
    status: 'active',
    reviewed: true,
  },
  alarm_group_sop_add_event: {
    groups: ['alarm_group'],
    description:
      'Snapshot uploaded from the escalation panel of the SOP workflow. action is the upload action.',
    properties: ['alarmGroupId', 'product', 'action'],
    status: 'active',
    reviewed: true,
  },
  open_feedback_modal: {
    groups: ['feedback'],
    description:
      'Opened feedback modal. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  submit_feedback: {
    groups: ['feedback'],
    description:
      'Submitted feedback. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
  cancel_feedback: {
    groups: ['feedback'],
    description:
      'Cancelled feedback. Generated from the event name; not yet checked against the code that fires it.',
    properties: [],
    status: 'active',
    reviewed: false,
  },
} as const satisfies Record<string, TrackingEventDefinition>;

export type TrackingEvent = keyof typeof TRACKING_EVENTS;

type EventsInGroup<G extends TrackingEventGroup> = {
  [K in TrackingEvent]: G extends (typeof TRACKING_EVENTS)[K]['groups'][number]
    ? K
    : never;
}[TrackingEvent];

export type EventAppNavigation = EventsInGroup<'app_navigation'>;
export type EventAlarm = EventsInGroup<'alarm'>;
export type EventLocationAlarm = EventsInGroup<'location_alarm'>;
export type EventTable = EventsInGroup<'table'>;
export type EventSidebar = EventsInGroup<'sidebar'>;
export type EventPage = EventsInGroup<'page'>;
export type EventFilter = EventsInGroup<'filter'>;
export type EventLocation = EventsInGroup<'location'>;
export type EventDoor = EventsInGroup<'door'>;
export type EventDoorGroup = EventsInGroup<'door_group'>;
export type EventAlarmTypeGroup = EventsInGroup<'alarm_type_group'>;
export type EventDemoAlarm = EventsInGroup<'demo_alarm'>;
export type EventAudioDevice = EventsInGroup<'audio_device'>;
export type EventCamera = EventsInGroup<'camera'>;
export type EventShareAlarm = EventsInGroup<'share_alarm'>;
export type EventStatusStepper = EventsInGroup<'status_stepper'>;
export type EventLocationContact = EventsInGroup<'location_contact'>;
export type EventLocationIncident = EventsInGroup<'location_incident'>;
export type EventMonitoring = EventsInGroup<'monitoring'>;
export type EventSOPWorkflow = EventsInGroup<'sop_workflow'>;
export type EventMonitoringWindow = EventsInGroup<'monitoring_window'>;
export type EventApartmentMonitoring = EventsInGroup<'apartment_monitoring'>;
export type AlarmGroupScan = EventsInGroup<'alarm_group_scan'>;
export type EventUnifiedMonitoring = EventsInGroup<'unified_monitoring'>;
export type EventAlarmGroup = EventsInGroup<'alarm_group'>;
export type EventFeedback = EventsInGroup<'feedback'>;
